import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { GlassCard } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Settings2, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import * as devicesApi from "@/services/devicesApi";
import { recordPppcActivityIfNew } from "@/services/pppcActivityLog";
import { getCurrentUserEmail } from "@/lib/auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface DeviceConfigurationsProps {
  deviceId: string;
}

const DeviceConfigurations = ({ deviceId }: DeviceConfigurationsProps) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const { data: policies = [], isLoading, error } = useQuery({
    queryKey: ["devicePolicies", deviceId],
    queryFn: async () => {
      const res = await devicesApi.getDevicePolicies(deviceId);
      console.log("[DeviceConfigurations] raw API response:", JSON.stringify(res, null, 2));
      // unwrap() already extracts the data array
      return Array.isArray(res) ? res : [];
    },
    enabled: !!deviceId,
  });

  // Safely read policy fields — API may use different casing/naming
  const getName = (p: any) =>
    p.name || p.policyName || p.displayName || p.title || "Unnamed Policy";
  const getId = (p: any) =>
    p.id || p.policyId || p.uuid;
  const getType = (p: any) =>
    p.type || p.policyType || p.kind || p.profileType;
  const getStatus = (p: any) =>
    p.status || p.installStatus || p.state;
  const getDate = (p: any) =>
    p.installedAt || p.installDate || p.createdAt || p.updatedAt;

  // Audit: the policy list returned by GET /devices/:id/policies is exactly
  // what the device's MDM agent receives. We record two PPPC events here:
  //
  //   1. "deployed" — the first time we observe a PPPC policy in a device's
  //      assigned-policy set (i.e., the agent has received the configuration
  //      via the policy-tag binding). Linked to the originating app
  //      (appId / bundleId) and the policy's tag set when the DTO carries
  //      them, so the per-app and per-device activity views can render the
  //      delivery trail.
  //   2. "installed" — when the backend further reports the profile is
  //      active/applied/installed on this device.
  //
  // Both writes are idempotent: recordPppcActivityIfNew dedupes by
  // (policyId, deviceId, action), so re-fetching the device page or
  // polling the policy list won't pile up duplicate entries.
  useEffect(() => {
    if (!deviceId || !Array.isArray(policies)) return;
    const isPppc = (name: string, type: string | undefined) =>
      typeof name === "string" && name.startsWith("PPPC — ") ||
      (typeof type === "string" && /pppc|tcc/i.test(type));
    const isInstalled = (status: string | undefined) =>
      typeof status === "string" &&
      ["installed", "active", "applied", "success"].includes(status.toLowerCase());

    // Resilient extractors — the policy DTO returned to the agent has
    // varied across backend versions (camelCase vs snake_case, tag IDs
    // sometimes nested under `tags` as objects).
    const getAppId = (p: any): string | undefined =>
      p.appId || p.applicationId || p.app_id;
    const getBundleId = (p: any): string | undefined =>
      p.bundleId || p.bundle_id || p.identifier;
    const getTagIds = (p: any): string[] | undefined => {
      const raw = p.tagIds ?? p.tag_ids ?? p.tags;
      if (!Array.isArray(raw)) return undefined;
      const ids = raw
        .map((t: any) => (typeof t === "string" ? t : t?.id ?? t?.tagId))
        .filter((v: unknown): v is string => typeof v === "string" && v.length > 0);
      return ids.length > 0 ? ids : undefined;
    };

    for (const p of policies) {
      const policyId = getId(p);
      const name = getName(p);
      const type = getType(p);
      const status = getStatus(p);
      if (!policyId || !isPppc(name, type)) continue;

      const appId = getAppId(p);
      const bundleId = getBundleId(p);
      const tagIds = getTagIds(p);

      // 1. Agent received the PPPC config (deployment event). Recorded
      //    on first observation regardless of install status.
      recordPppcActivityIfNew({
        policyId,
        deviceId,
        appId,
        bundleId,
        tagIds,
        action: "deployed",
        actor: getCurrentUserEmail(),
        summary: `${name} delivered to device`,
        details: { source: "DeviceConfigurations", status: status ?? null },
        at: getDate(p) ?? undefined,
      });

      // 2. Backend confirms the profile is installed/active on the device.
      if (isInstalled(status)) {
        recordPppcActivityIfNew({
          policyId,
          deviceId,
          appId,
          bundleId,
          tagIds,
          action: "installed",
          actor: getCurrentUserEmail(),
          summary: `${name} confirmed installed on device`,
          details: { status, source: "DeviceConfigurations" },
          at: getDate(p) ?? undefined,
        });
      }
    }
    // We intentionally only re-run when the policies list identity changes,
    // not the device id (the query is already keyed by deviceId).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policies, deviceId]);


  return (
    <GlassCard variant="default" hover={false} className={cn(isMobile ? "p-3" : "p-6")}>
      <h2 className={cn(
        "font-semibold text-foreground mb-3 sm:mb-4",
        isMobile ? "text-base" : "text-xl"
      )}>
        Device Configurations
      </h2>

      {isLoading && (
        <div className={cn(
          "flex items-center justify-center text-muted-foreground",
          isMobile ? "py-6 text-sm" : "py-12"
        )}>
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading configurations…
        </div>
      )}

      {error && (
        <div className={cn(
          "flex items-center justify-center text-destructive gap-2",
          isMobile ? "py-6" : "py-12"
        )}>
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load configurations.</span>
        </div>
      )}

      {!isLoading && !error && policies.length === 0 && (
        <div className={cn("text-center text-muted-foreground", isMobile ? "py-6" : "py-12")}>
          <Settings2 className={cn(
            "mx-auto mb-3 text-muted-foreground/50",
            isMobile ? "h-8 w-8" : "h-12 w-12"
          )} />
          <p className="text-sm">No configurations assigned to this device.</p>
        </div>
      )}

      {!isLoading && !error && policies.length > 0 && (
        <div className={cn(isMobile ? "space-y-2" : "space-y-3")}>
          {policies.map((policy: any, idx: number) => {
            const policyId = getId(policy);
            const policyName = getName(policy);
            const policyType = getType(policy);
            const policyStatus = getStatus(policy);
            const policyDate = getDate(policy);

            return (
              <div
                key={policyId || idx}
                role="button"
                tabIndex={0}
                aria-label={`Open configuration ${policyName}${policyStatus ? `, status ${policyStatus}` : ""}`}
                className={cn(
                  "flex items-center justify-between border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer min-h-[44px]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  isMobile ? "p-3 gap-2" : "p-4"
                )}
                onClick={() => policyId && navigate(`/configurations/${policyId}`)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && policyId) {
                    e.preventDefault();
                    navigate(`/configurations/${policyId}`);
                  }
                }}
              >
                <div className={cn("flex items-center min-w-0", isMobile ? "gap-2" : "gap-3")}>
                  <Settings2 className={cn(
                    "flex-shrink-0 text-muted-foreground",
                    isMobile ? "h-4 w-4" : "h-5 w-5"
                  )} />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{policyName}</p>
                    {policyType && (
                      <p className="text-xs text-muted-foreground truncate">{policyType}</p>
                    )}
                  </div>
                </div>
                <div className={cn("flex items-center flex-shrink-0", isMobile ? "gap-1.5" : "gap-2")}>
                  {policyStatus && (
                    <Badge
                      variant={policyStatus === "installed" || policyStatus === "active" ? "default" : "outline"}
                      className="text-xs"
                    >
                      {policyStatus}
                    </Badge>
                  )}
                  {policyDate && !isMobile && (
                    <span className="text-xs text-muted-foreground">
                      {new Date(policyDate).toLocaleDateString()}
                    </span>
                  )}
                  <ExternalLink className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </GlassCard>
  );
};

export default DeviceConfigurations;
